import React, { useMemo, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import {
  GridActionsCellItem,
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid-premium';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Fab from '@mui/material/Fab';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { AsyncDialog } from '../../components/AsyncDialog';
import { AsyncDialogRef } from '../../components/AsyncDialog/AsyncDialog';
import { ConfirmationDialog, ConfirmationDialogData } from '../../components/ConfirmationDialog';
import { Table } from '../../components/Table';
import { usePermissions } from '../../hooks';
import {
  useDeleteBankLookupMutation,
  useFetchBankLookupsQuery,
} from '../../redux/features/bankLookup/bankLookup.apiSlice';
import { BankLookup } from '../../types/bankLookup';
import * as permissions from '../../utils/permissions';
import { BankLookupDialog } from './BankLookupDialog';

/** For now only the logo can be edited: adding, deleting and changing a bank's details are switched off. */
const BANK_DETAILS_EDITABLE = false;

const BASE_COLUMNS: GridColDef<BankLookup>[] = [
  {
    field: 'logoUrl',
    headerName: 'Logo',
    width: 70,
    sortable: false,
    filterable: false,
    renderCell: ({ row }: GridRenderCellParams<BankLookup>) => (
      <Avatar
        src={row.logoUrl || undefined}
        alt=""
        variant="rounded"
        sx={{ width: 40, height: 40, bgcolor: '#ccc', '& img': { objectFit: 'contain' } }}
      >
        {row.logoUrl ? null : row.bankName?.[0] ?? '—'}
      </Avatar>
    ),
  },
  { field: 'id', headerName: 'Id', width: 70 },
  { field: 'bankName', headerName: 'Name En', flex: 1, minWidth: 200 },
  {
    field: 'bankNameAr',
    headerName: 'Name Ar',
    flex: 1,
    minWidth: 200,
    renderCell: ({ value }) => <Box dir="rtl">{value}</Box>,
  },
  { field: 'countryName', headerName: 'Country En', width: 160 },
  {
    field: 'countryNameAr',
    headerName: 'Country Ar',
    width: 180,
    renderCell: ({ value }) => <Box dir="rtl">{value}</Box>,
  },
  { field: 'bic', headerName: 'BIC', width: 130, renderCell: ({ value }) => value && <code>{value}</code> },
  { field: 'bankCode', headerName: 'Bank code', width: 100 },
];

export function BankLookupList() {
  const { enqueueSnackbar } = useSnackbar();
  const asyncDialogRef = useRef<AsyncDialogRef<ConfirmationDialogData, boolean>>(null);
  const { data: banks = [], isFetching, isError } = useFetchBankLookupsQuery();
  const [deleteBank] = useDeleteBankLookupMutation();
  const [open, setOpen] = useState(false);
  const [editValue, setEditValue] = useState<BankLookup | null>(null);
  const canCreate = usePermissions({ items: [permissions.BankLookup.create] });
  const canUpdate = usePermissions({ items: [permissions.BankLookup.update] });
  const canDelete = usePermissions({ items: [permissions.BankLookup.delete] });

  const onAddNew = (): void => {
    setEditValue(null);
    setOpen(true);
  };

  const onEditBank = (row: BankLookup): void => {
    setEditValue(row);
    setOpen(true);
  };

  const onDeleteBank = async (row: BankLookup): Promise<void> => {
    await asyncDialogRef.current?.show({
      title: 'Delete bank',
      description: `Delete ${row.bankName}? This is permanent and cannot be undone.`,
      acceptButtonText: 'Delete',
      onConfirm: async () => {
        try {
          await deleteBank(row.id).unwrap();
          enqueueSnackbar('Bank deleted.', { variant: 'success' });
        } catch (err: any) {
          enqueueSnackbar(
            err?.data?.message === 'BANK_LOOKUP_NOT_FOUND'
              ? 'This bank no longer exists.'
              : err?.data?.message || 'The bank could not be deleted.',
            { variant: 'error' },
          );
          throw err;
        }
      },
    });
  };

  const handleClose = () => {
    setEditValue(null);
    setOpen(false);
  };

  const columns = useMemo<GridColDef<BankLookup>[]>(() => {
    if (!canUpdate && !canDelete) {
      return BASE_COLUMNS;
    }

    const actionColumn: GridColDef<BankLookup> = {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 90,
      getActions: ({ row }: GridRowParams<BankLookup>) => {
        const actions: React.ReactElement[] = [];

        if (canUpdate) {
          actions.push(
            <GridActionsCellItem
              key={`bank-lookup-update-${row.id}`}
              icon={<EditOutlinedIcon />}
              label="Edit logo"
              onClick={() => onEditBank(row)}
            />,
          );
        }

        if (canDelete) {
          actions.push(
            <GridActionsCellItem
              key={`bank-lookup-delete-${row.id}`}
              icon={<DeleteOutlineOutlinedIcon />}
              label="Delete"
              disabled={!BANK_DETAILS_EDITABLE}
              onClick={() => onDeleteBank(row)}
            />,
          );
        }

        return actions;
      },
    };

    return [actionColumn, ...BASE_COLUMNS];
  }, [canUpdate, canDelete]);

  return (
    <Container component="main" maxWidth="xl">
      <Box sx={{ pb: 5 }}>
        <Typography variant="h4">
          Banks &nbsp;
          {canCreate && (
            <Fab
              color="primary"
              size="small"
              aria-label="Add bank"
              disabled={!BANK_DETAILS_EDITABLE}
              onClick={onAddNew}
            >
              <AddIcon />
            </Fab>
          )}
        </Typography>
      </Box>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Could not load the banks.
        </Alert>
      )}

      <BankLookupDialog open={open} onClose={handleClose} bank={editValue} logoOnly={!BANK_DETAILS_EDITABLE} />

      <Table
        rows={banks}
        columns={columns}
        loading={isFetching}
        paginationMode="client"
        sortingMode="client"
        filterMode="client"
        initialSortModel={[{ field: 'bankName', sort: 'asc' }]}
        customToolbarItems={<GridToolbarQuickFilter sx={{ ml: 'auto' }} />}
        rowCount={banks.length}
      />

      <AsyncDialog ref={asyncDialogRef} dialog={ConfirmationDialog} />
    </Container>
  );
}
